import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faBriefcase } from "@fortawesome/pro-solid-svg-icons/faBriefcase";
import { faPresentation } from "@fortawesome/pro-solid-svg-icons/faPresentation";
import { faUserGraduate } from "@fortawesome/pro-solid-svg-icons/faUserGraduate";
import { faUserHardHat } from "@fortawesome/pro-solid-svg-icons/faUserHardHat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, CardContent, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { GlobalClassKeys } from "assets/jss/common-styles";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiTextFieldColor from "components/MuiExtras/CustomInput/MuiTextFieldColor";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiAvatar from "components/MuiExtras/MuiAvatar";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiLink from "components/MuiExtras/MuiLink";
import { MuiColors } from "components/MuiExtras/Typography/MuiColor";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { i18n } from "i18next";
import { Formaters } from "lib/Formarters";
import { UserCredentialType } from "models/ApiEnums";
import { UserCredentialsModel } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import UserCredentialEditDrawer from "views/User/Dialogs/UserCredentialEditDrawer";

/////////////////////////////////////////////////////////////////////////////////////

type CredentialRenderValues = {
    subTitle: string;
    icon: IconDefinition;
    iconTitle: string;
    bgColor: MuiColors;
};

export function getCredentialsRenderValues(i18n: i18n, c: UserCredentialsModel): CredentialRenderValues {

    const subTitle = (c.startDate && c.endDate)
        ? (c.startDate.getFullYear() == c.endDate.getFullYear() && c.startDate.getMonth() == c.endDate.getMonth())
            ? "(" + Formaters.monthYear(c.endDate) + ")"
            : "(" + Formaters.monthYear(c.startDate, true) + " - " + Formaters.monthYear(c.endDate) + ")"
        : (c.startDate && !c.endDate)
            ? "(" + Formaters.monthYear(c.startDate) + " - " + i18n.t("Glossary.Currently") + ")"
            : (!c.startDate && c.endDate)
                ? "(" + i18n.t("Glossary.Until") + " " + Formaters.monthYear(c.endDate) + ")"
                : null;

    const icon = c.type == UserCredentialType.AcademicFormation
        ? faUserGraduate
        : c.type == UserCredentialType.ProfessionQualification
            ? faBriefcase
            : c.type == UserCredentialType.Project
                ? faPresentation
                : faUserHardHat;

    const iconTitle = c.type == UserCredentialType.AcademicFormation
        ? i18n.t("sys:UserCredentialType.AcademicFormation")
        : c.type == UserCredentialType.ProfessionQualification
            ? i18n.t("sys:UserCredentialType.ProfessionQualification")
            : c.type == UserCredentialType.Project
                ? i18n.t("sys:UserCredentialType.Project")
                : i18n.t("sys:UserCredentialType.Hobby");

    const bgColor: MuiColors = c.type == UserCredentialType.AcademicFormation
        ? "info"
        : c.type == UserCredentialType.ProfessionQualification
            ? "warning"
            : c.type == UserCredentialType.Project
                ? "success"
                : "mutedDark";


    return {
        subTitle,
        icon,
        iconTitle,
        bgColor
    };
}

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    credentials: UserCredentialsModel[];
    onChange: (data: UserCredentialsModel[]) => void;
}

interface IState {
    searchText: string;
    dialog: any;
    mouseOver: number;
}

type Props = IProps & WithSnackbar & WithTranslation;


class UserEditCredentials extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            searchText: "",
            dialog: null,
            mouseOver: null
        };
    }

    ///
    private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: e.currentTarget.value });
    };

    ///
    private handleFilterClear = () => {
        this.setState({ searchText: "" });
    };

    ///
    private handleAdd = () => {
        const { credentials } = this.props;

        this.setState({
            dialog: <UserCredentialEditDrawer
                title="Nova Credencial"
                credential={
                    {
                        index: credentials.length + 1,
                        type: UserCredentialType.ProfessionQualification,
                        description: null,
                        startDate: null,
                        endDate: null
                    }
                }
                onClose={
                    (credential) => {
                        if (credential != null) {
                            credentials.push(credential);
                            this.props.onChange(credentials);
                        }
                        this.setState({ dialog: null });
                    }
                }
            />
        });
    };

    ///
    private handleEdit(credential: UserCredentialsModel) {
        const credentials = this.props.credentials.slice(0);

        this.setState({
            dialog: <UserCredentialEditDrawer
                title="Editar Credencial"
                credential={credential}
                onClose={
                    (credential) => {
                        if (credential != null) {
                            var i = credentials.findIndex(p => p.index == credential.index);
                            credentials[i] = credential;
                            this.props.onChange(credentials);
                        }
                        this.setState({ dialog: null });
                    }
                }
            />
        });
    }

    ///
    private handleDelete(item: UserCredentialsModel) {
        this.setState({
            dialog: <MuiConfirmation
                message={this.props.t("Views.User.Components.UserEditCredentials.ConfirmationDeleteMessage", { description: item.description })}
                onClose={
                    (result) => {
                        if (result) this.props.onChange(this.props.credentials.filter(p => p.index != item.index));
                        this.setState({ dialog: null });
                    }
                }
            />
        });
    }

    ///
    render() {
        const credentials = this.state.searchText == ""
            ? this.props.credentials
            : this.props.credentials.filter(f => f.description.toLowerCase().indexOf(this.state.searchText.toLowerCase()) > -1);

        return (
            <>
                {this.state.dialog}

                <Card>
                    <CardHeaderColor color="primary"
                        title={
                            <>
                                <Grid container={true} justifyContent={"space-between"} >
                                    <Grid item>
                                        <IconButtonAdd
                                            color="inherit"
                                            title={this.props.t("Views.User.Components.UserEditCredentials.ButtonAddTitle")}
                                            onClick={this.handleAdd}
                                        />

                                    </Grid>

                                    <GridEx item alignSelf="center">
                                        <Typography variant="h6">
                                            <span>{this.props.t("Views.User.Components.UserEditCredentials.HeaderTitle")}</span>
                                        </Typography>
                                    </GridEx>

                                    <Grid>
                                    </Grid>
                                </Grid>

                                <Grid container={true} justifyContent={"space-between"} >
                                    <GridEx item xs>
                                        <MuiTextFieldColor
                                            fullWidth
                                            placeholder={this.props.t("Common.Input.Label.SearchFor")}
                                            inputColor="inherit"
                                            underlineColor="inherit"
                                            value={this.state.searchText}
                                            onChange={this.handleFilterChange}
                                            endAdornment={
                                                this.state.searchText == ""
                                                    ? null
                                                    : <IconButtonCancel onClick={this.handleFilterClear} />
                                            }
                                        />
                                    </GridEx>
                                </Grid>
                            </>
                        }
                    />

                    <CardContent>
                        {
                            credentials.length == 0
                                ? <Typography variant="subtitle1">{this.props.t("Views.User.Components.UserEditCredentials.EmptyData")}</Typography>
                                : credentials.map((c, index) => {

                                    const p = getCredentialsRenderValues(this.props.i18n, c);

                                    return (
                                        <Grid
                                            key={index}
                                            container
                                            spacing={1}
                                            justifyContent="space-between"
                                            alignItems="center"
                                            wrap="nowrap"
                                            className={this.state.mouseOver == index ? GlobalClassKeys.overElementBackground : null}
                                            onMouseEnter={() => this.setState({ mouseOver: index })}
                                            onMouseLeave={() => this.setState({ mouseOver: null })}
                                        >
                                            <Grid item>
                                                <MuiAvatar backgroundColor={p.bgColor}>
                                                    <FontAwesomeIcon icon={p.icon} title={p.iconTitle} />
                                                </MuiAvatar>
                                            </Grid>
                                            <GridEx item xs zeroMinWidth alignSelf="center">
                                                <Typography noWrap variant="subtitle2">
                                                    <MuiLink onClick={() => this.handleEdit(c)}>{c.description}</MuiLink>
                                                </Typography>
                                                {p.subTitle &&
                                                    <Typography noWrap variant="subtitle2" color="textSecondary"><small>
                                                        {p.subTitle}
                                                    </small></Typography>
                                                }
                                            </GridEx>
                                            <Grid item>
                                                <IconButtonDelete size="small" color="muted" onClick={() => this.handleDelete(c)} />
                                            </Grid>
                                        </Grid>
                                    );
                                })
                        }
                    </CardContent>

                </Card>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(UserEditCredentials));